import type { VariantProps } from 'class-variance-authority'
import type { HTMLAttributes, InjectionKey, Ref } from 'vue'
import type { ButtonVariants } from '@ds/primitives/button'
import { cva } from 'class-variance-authority'
import { InputSize } from '../input'

export type { InputSize }

/**
 * Set once on `DSInputGroup` (defaults to `md`, same as `DSInput`) and read by every piece
 * inside it — `DSInputGroupInput`/`DSInputGroupAddon`/`DSInputGroupButton`/`DSInputGroupText` —
 * so a caller sets density once at the group instead of on each child.
 */
export const INPUT_GROUP_SIZE: InjectionKey<Ref<InputSize>> = Symbol('input-group-size')

export const INPUT_GROUP_SIZE_CLASS: Record<InputSize, string> = {
  sm: 'h-[2.25rem]',
  md: 'h-10',
  lg: 'h-[2.875rem]',
}

export { default as InputGroup } from './InputGroup.vue'
export { default as InputGroupAddon } from './InputGroupAddon.vue'
export { default as InputGroupButton } from './InputGroupButton.vue'
export { default as InputGroupInput } from './InputGroupInput.vue'
export { default as InputGroupText } from './InputGroupText.vue'
export { default as InputGroupTextarea } from './InputGroupTextarea.vue'

export const inputGroupAddonVariants = cva(
  'text-muted-foreground flex h-auto cursor-text items-center justify-center font-medium select-none [&>kbd]:rounded-ds-sm group-data-[disabled=true]/input-group:opacity-50',
  {
    variants: {
      align: {
        'inline-start':
          'order-first pl-2.5 has-[>button]:ml-[-0.45rem] has-[>kbd]:ml-[-0.35rem]',
        'inline-end':
          'order-last pr-2.5 has-[>button]:mr-[-0.45rem] has-[>kbd]:mr-[-0.35rem]',
        'block-start':
          'order-first w-full justify-start px-2.5 pt-3 [.border-b]:pb-3 group-has-[>input]/input-group:pt-2.5',
        'block-end':
          'order-last w-full justify-start px-2.5 pb-3 [.border-t]:pt-3 group-has-[>input]/input-group:pb-2.5',
      },
      // Same scale as `DSInput`'s text sizes — gap/icon size step with it.
      size: {
        sm: 'gap-0.5 py-1 text-xs [&>svg]:size-3.75',
        md: 'gap-2 py-1.5 text-sm [&>svg]:size-4',
        lg: 'gap-2.5 py-2 text-base [&>svg]:size-5',
      },
    },
    defaultVariants: {
      align: 'inline-start',
      size: 'md',
    },
  },
)

export type InputGroupVariants = VariantProps<typeof inputGroupAddonVariants>


export interface InputGroupButtonProps {
  variant?: ButtonVariants['variant']
  size?: InputSize
  class?: HTMLAttributes['class']
}
